import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Compass, RefreshCw, Settings, Star, X, Check, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/constants';
import BottomNav from '../components/BottomNav';
import CigarImage from '../components/CigarImage';
import { toast } from 'sonner';

const DiscoverPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [hasPreferences, setHasPreferences] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  
  // Load saved filters from localStorage
  const getSavedFilters = () => {
    try {
      const saved = localStorage.getItem('discoverFilters');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Error loading saved filters:', e);
    }
    return {
      strength: '',
      origin: '',
      wrapper: '',
      priceRange: '',
      minRating: '',
      flavors: []
    };
  };

  // Filter preferences - initialize from localStorage
  const [filters, setFilters] = useState(getSavedFilters);
  const [tempFilters, setTempFilters] = useState(filters);
  
  // Refresh seed for randomization
  const [refreshSeed, setRefreshSeed] = useState(Date.now());

  // Save filters to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('discoverFilters', JSON.stringify(filters));
    } catch (e) {
      console.error('Error saving filters:', e);
    }
  }, [filters]);

  const fetchRecommendations = useCallback(async (seed = refreshSeed, appliedFilters = filters) => {
    if (!token) return;
    
    try {
      const params = new URLSearchParams({ limit: 20, refresh_seed: seed });
      if (appliedFilters.strength) params.append('strength', appliedFilters.strength);
      if (appliedFilters.origin) params.append('origin', appliedFilters.origin);
      if (appliedFilters.wrapper) params.append('wrapper', appliedFilters.wrapper);
      if (appliedFilters.priceRange) params.append('price_range', appliedFilters.priceRange);
      if (appliedFilters.minRating) params.append('min_rating', appliedFilters.minRating);
      // Support multiple flavors
      if (appliedFilters.flavors && appliedFilters.flavors.length > 0) {
        appliedFilters.flavors.forEach(f => params.append('flavor', f));
      }
      
      const res = await axios.get(`${API}/cigars/discover?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setRecommendations(res.data.recommendations);
      setHasPreferences(res.data.has_preferences);
      
      // Show onboarding if user has no preferences and no filters set
      // AND user hasn't skipped onboarding before
      const hasSkippedOnboarding = localStorage.getItem('discoverOnboardingSkipped') === 'true';
      if (!hasSkippedOnboarding && !res.data.has_preferences && !appliedFilters.strength && !appliedFilters.origin && !appliedFilters.wrapper && !appliedFilters.priceRange && !appliedFilters.minRating && (!appliedFilters.flavors || appliedFilters.flavors.length === 0)) {
        setShowOnboarding(true);
      }
    } catch (error) {
      console.error('Failed to fetch recommendations');
      toast.error('Failed to load recommendations');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, refreshSeed, filters]);

  useEffect(() => {
    if (token) {
      // Generate new seed on each page load for variety
      const newSeed = Date.now();
      setRefreshSeed(newSeed);
      fetchRecommendations(newSeed, filters);
    } else {
      setLoading(false);
    }
  }, [token]);

  const handleRefresh = () => {
    setRefreshing(true);
    const newSeed = Date.now();
    setRefreshSeed(newSeed);
    fetchRecommendations(newSeed, filters);
  };

  const handleApplyFilters = () => {
    setFilters(tempFilters);
    setShowSettings(false);
    setShowOnboarding(false);
    setLoading(true);
    const newSeed = Date.now();
    setRefreshSeed(newSeed);
    fetchRecommendations(newSeed, tempFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters = { strength: '', origin: '', wrapper: '', priceRange: '', minRating: '', flavors: [] };
    setTempFilters(clearedFilters);
    setFilters(clearedFilters);
    setShowSettings(false);
    setLoading(true);
    const newSeed = Date.now();
    setRefreshSeed(newSeed);
    fetchRecommendations(newSeed, clearedFilters);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0B0B0C] flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center">
            <Compass className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl text-white mb-2">Discover New Cigars</h2>
            <p className="text-white/60 mb-6">Sign in to get personalized recommendations</p>
            <Button onClick={() => navigate('/auth')} className="bg-amber-600 hover:bg-amber-700">
              Sign In
            </Button>
          </div>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0C] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <Compass className="text-amber-500" size={24} />
        <h1 className="playfair text-xl text-white">
          Discover New Cigars
        </h1>
        <div className="flex items-center gap-2">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2 text-white/60 hover:text-amber-500 transition-colors disabled:opacity-50"
            data-testid="discover-refresh-btn"
          >
            <RefreshCw size={20} className={refreshing ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => {
              setTempFilters(filters);
              setShowSettings(true);
            }}
            className="p-2 text-white/60 hover:text-amber-500 transition-colors"
            data-testid="discover-settings-btn"
          >
            <Settings size={20} />
          </button>
        </div>
      </div>

      {/* Active Filters Display */}
      {(filters.strength || filters.origin || filters.wrapper || filters.priceRange || filters.minRating || (filters.flavors && filters.flavors.length > 0)) && (
        <div className="px-4 py-2 border-b border-white/5 flex items-center gap-2 flex-wrap">
          <span className="text-white/40 text-sm">Filters:</span>
          {filters.strength && (
            <span className="bg-amber-500/20 text-amber-500 text-xs px-2 py-1 rounded-full">{filters.strength}</span>
          )}
          {filters.origin && (
            <span className="bg-amber-500/20 text-amber-500 text-xs px-2 py-1 rounded-full">{filters.origin}</span>
          )}
          {filters.wrapper && (
            <span className="bg-amber-500/20 text-amber-500 text-xs px-2 py-1 rounded-full">{filters.wrapper}</span>
          )}
          {filters.priceRange && (
            <span className="bg-amber-500/20 text-amber-500 text-xs px-2 py-1 rounded-full">{filters.priceRange}</span>
          )}
          {filters.minRating && (
            <span className="bg-amber-500/20 text-amber-500 text-xs px-2 py-1 rounded-full">{filters.minRating}+ rated</span>
          )}
          {filters.flavors && filters.flavors.map(flavor => (
            <span key={flavor} className="bg-amber-500/20 text-amber-500 text-xs px-2 py-1 rounded-full">{flavor}</span>
          ))}
          <button onClick={handleClearFilters} className="text-white/40 hover:text-white text-xs ml-2">
            Clear all
          </button>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 pb-24">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="animate-spin text-amber-500" size={32} />
          </div>
        ) : recommendations.length === 0 ? (
          <div className="text-center py-12">
            <Compass className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <p className="text-white/60">No recommendations found</p>
            <p className="text-white/40 text-sm mt-2">Try adjusting your filters or refresh</p>
            <Button 
              onClick={() => setShowSettings(true)} 
              variant="outline" 
              className="mt-4 border-amber-500/50 text-amber-500"
            >
              Adjust Preferences
            </Button>
          </div>
        ) : (
          <>
            {hasPreferences && (
              <p className="text-white/50 text-sm mb-4 text-center">
                Based on your favorites and ratings
              </p>
            )}
            <div className="space-y-3">
              {recommendations.map((cigar) => (
                <div
                  key={cigar.id}
                  onClick={() => navigate(`/cigar/${cigar.id}`)}
                  className="bg-white/5 rounded-xl p-3 sm:p-4 cursor-pointer hover:bg-white/10 transition-all"
                  data-testid="discover-cigar-card"
                >
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 bg-white/5 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                      <CigarImage src={cigar.image_url} alt={cigar.name} />
                    </div>
                    
                    <div className="flex-1 min-w-0 pr-2">
                      <p className="text-xs text-white/50 truncate">{cigar.brand}</p>
                      <p className="text-white font-semibold truncate text-sm sm:text-base">
                        {cigar.name || cigar.brand}
                      </p>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1 text-xs text-white/40">
                        {cigar.strength && <span className="whitespace-nowrap">{cigar.strength}</span>}
                        {cigar.origin && <span className="whitespace-nowrap">• {cigar.origin}</span>}
                      </div>
                      {cigar.price_range && (
                        <p className="text-xs text-amber-500/80 mt-0.5">{cigar.price_range}</p>
                      )}
                    </div>
                    
                    <div className={`flex items-center gap-1 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg flex-shrink-0 ${
                      cigar.average_rating > 0 ? 'bg-amber-500/20' : 'bg-white/5'
                    }`}>
                      <Star className={`${cigar.average_rating > 0 ? 'text-amber-500 fill-amber-500' : 'text-white/30'}`} size={14} />
                      <span className={`font-bold text-sm ${cigar.average_rating > 0 ? 'text-amber-500' : 'text-white/30'}`}>
                        {cigar.average_rating > 0 ? cigar.average_rating.toFixed(1) : '—'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <SettingsModal
          filters={tempFilters}
          setFilters={setTempFilters}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
          onClose={() => setShowSettings(false)}
        />
      )}

      {/* Onboarding Modal */}
      {showOnboarding && !showSettings && (
        <OnboardingModal
          filters={tempFilters}
          setFilters={setTempFilters}
          onApply={handleApplyFilters}
          onSkip={() => {
            setShowOnboarding(false);
            // Mark that user has skipped onboarding so it doesn't show again
            localStorage.setItem('discoverOnboardingSkipped', 'true');
            // Just show random cigars
            handleRefresh();
          }}
        />
      )}

      <BottomNav />
    </div>
  );
};

// Settings Modal Component
const SettingsModal = ({ filters, setFilters, onApply, onClear, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-[#141416] w-full max-w-lg rounded-2xl p-6 max-h-[85vh] overflow-y-auto animate-slide-up">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl text-white font-semibold">Discover Preferences</h2>
          <button onClick={onClose} className="text-white/60 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <div className="space-y-6">
          <FilterSelect
            label="Strength"
            value={filters.strength}
            onChange={(v) => setFilters({ ...filters, strength: v })}
            options={['', 'mild', 'medium', 'medium-full', 'full']}
            placeholder="Any strength"
          />

          <FilterSelect
            label="Origin"
            value={filters.origin}
            onChange={(v) => setFilters({ ...filters, origin: v })}
            options={['', 'Nicaragua', 'Dominican Republic', 'Honduras', 'Cuba', 'Mexico', 'Ecuador']}
            placeholder="Any origin"
          />

          <FilterSelect
            label="Wrapper"
            value={filters.wrapper}
            onChange={(v) => setFilters({ ...filters, wrapper: v })}
            options={['', 'Habano', 'Maduro', 'Connecticut', 'Corojo', 'Sumatra', 'Cameroon', 'San Andres']}
            placeholder="Any wrapper"
          />

          <FilterSelect
            label="Price Range"
            value={filters.priceRange}
            onChange={(v) => setFilters({ ...filters, priceRange: v })}
            options={['', 'Under $10', '$10-15', '$15-25', '$25-35', 'Over $35']}
            placeholder="Any price"
          />

          <FilterSelect
            label="Minimum Rating"
            value={filters.minRating}
            onChange={(v) => setFilters({ ...filters, minRating: v })}
            options={['', '6', '7', '8', '9']}
            placeholder="Any rating"
            displayMap={{ '6': '6+ stars', '7': '7+ stars', '8': '8+ stars', '9': '9+ stars' }}
          />

          <FlavorMultiSelect
            label="Flavor Profile"
            values={filters.flavors || []}
            onChange={(v) => setFilters({ ...filters, flavors: v })}
            options={['cedar', 'leather', 'chocolate', 'coffee', 'pepper', 'earth', 'nuts', 'cream', 'spice', 'vanilla', 'honey']}
            placeholder="Select up to 3 flavors"
            maxSelections={3}
          />
        </div>

        <div className="flex gap-3 mt-8">
          <Button
            onClick={onClear}
            variant="outline"
            className="flex-1 border-white/20 text-white/70"
          >
            Clear All
          </Button>
          <Button
            onClick={onApply}
            className="flex-1 bg-amber-600 hover:bg-amber-700"
          >
            Apply Filters
          </Button>
        </div>
      </div>
    </div>
  );
};

// Onboarding Modal Component
const OnboardingModal = ({ filters, setFilters, onApply, onSkip }) => {
  const [step, setStep] = useState(0);
  const steps = [
    {
      title: "What strength do you prefer?",
      key: 'strength',
      options: [
        { value: 'mild', label: 'Mild', desc: 'Smooth & light' },
        { value: 'medium', label: 'Medium', desc: 'Balanced flavor' },
        { value: 'medium-full', label: 'Medium-Full', desc: 'Rich & bold' },
        { value: 'full', label: 'Full', desc: 'Intense & powerful' }
      ]
    },
    {
      title: "Preferred origin?",
      key: 'origin',
      options: [
        { value: 'Nicaragua', label: 'Nicaragua', desc: 'Bold & spicy' },
        { value: 'Dominican Republic', label: 'Dominican', desc: 'Smooth & refined' },
        { value: 'Honduras', label: 'Honduras', desc: 'Earthy & rich' },
        { value: '', label: 'No preference', desc: 'Show me all' }
      ]
    },
    {
      title: "Favorite flavor notes?",
      key: 'flavors',
      isMulti: true,
      options: [
        { value: 'cedar', label: 'Cedar & Wood', desc: 'Classic cigar taste' },
        { value: 'chocolate', label: 'Chocolate', desc: 'Sweet & rich' },
        { value: 'coffee', label: 'Coffee', desc: 'Roasted & bold' },
        { value: 'pepper', label: 'Pepper & Spice', desc: 'Kick of heat' }
      ]
    }
  ];

  const currentStep = steps[step];

  const handleSelect = (value) => {
    if (currentStep.isMulti) {
      // For multi-select step, toggle the value
      const currentFlavors = filters.flavors || [];
      let newFlavors;
      if (currentFlavors.includes(value)) {
        newFlavors = currentFlavors.filter(f => f !== value);
      } else if (currentFlavors.length < 3) {
        newFlavors = [...currentFlavors, value];
      } else {
        return; // Max 3 reached
      }
      setFilters({ ...filters, flavors: newFlavors });
    } else {
      setFilters({ ...filters, [currentStep.key]: value });
      
      if (step < steps.length - 1) {
        setStep(step + 1);
      } else {
        onApply();
      }
    }
  };

  const handleContinue = () => {
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      onApply();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
      <div className="bg-[#141416] w-full max-w-md rounded-2xl p-6 animate-fade-in">
        <div className="text-center mb-6">
          <Compass className="w-12 h-12 text-amber-500 mx-auto mb-3" />
          <h2 className="text-xl text-white font-semibold mb-2">
            {currentStep.title}
          </h2>
          <p className="text-white/50 text-sm">
            Step {step + 1} of {steps.length}
            {currentStep.isMulti && ' (select up to 3)'}
          </p>
        </div>

        <div className="space-y-3">
          {currentStep.options.map((option) => {
            const isSelected = currentStep.isMulti 
              ? (filters.flavors || []).includes(option.value)
              : filters[currentStep.key] === option.value;
            
            return (
              <button
                key={option.value}
                onClick={() => handleSelect(option.value)}
                className={`w-full border rounded-xl p-4 text-left transition-all ${
                  isSelected 
                    ? 'bg-amber-500/20 border-amber-500/50' 
                    : 'bg-white/5 hover:bg-white/10 border-white/10 hover:border-amber-500/50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">{option.label}</p>
                    <p className="text-white/50 text-sm">{option.desc}</p>
                  </div>
                  {isSelected && <Check className="text-amber-500" size={20} />}
                </div>
              </button>
            );
          })}
        </div>

        {currentStep.isMulti && (
          <Button
            onClick={handleContinue}
            className="w-full mt-4 bg-amber-600 hover:bg-amber-700"
          >
            {(filters.flavors || []).length === 0 ? 'Skip' : 'Continue'}
          </Button>
        )}

        <button
          data-testid="skip-onboarding-button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log('Skip button clicked');
            onSkip();
          }}
          className="w-full mt-4 text-white/50 hover:text-white text-sm transition-colors py-3 border border-white/10 rounded-lg hover:border-white/30 hover:bg-white/5"
        >
          Skip for now
        </button>

        {/* Progress dots */}
        <div className="flex justify-center gap-2 mt-4">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`w-2 h-2 rounded-full transition-colors ${
                i === step ? 'bg-amber-500' : i < step ? 'bg-amber-500/50' : 'bg-white/20'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// Filter Select Component - Fixed to stay within viewport
const FilterSelect = ({ label, value, onChange, options, placeholder, displayMap = {} }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState('bottom');
  const buttonRef = React.useRef(null);

  const handleOpen = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      // If less than 200px below, show dropdown above
      setDropdownPosition(spaceBelow < 200 && spaceAbove > spaceBelow ? 'top' : 'bottom');
    }
    setIsOpen(!isOpen);
  };

  // Get display text for a value
  const getDisplayText = (val) => {
    if (!val) return placeholder;
    return displayMap[val] || val;
  };

  return (
    <div>
      <label className="block text-white/70 text-sm mb-2">{label}</label>
      <div className="relative">
        <button
          ref={buttonRef}
          onClick={handleOpen}
          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-left flex items-center justify-between hover:border-white/20 transition-colors"
          data-testid={`filter-select-${label.toLowerCase().replace(' ', '-')}`}
        >
          <span className={value ? 'text-white' : 'text-white/40'}>
            {getDisplayText(value)}
          </span>
          <ChevronDown size={18} className={`text-white/40 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
            <div 
              className={`absolute left-0 right-0 bg-[#1a1a1c] border border-white/10 rounded-lg shadow-xl z-20 max-h-48 overflow-y-auto ${
                dropdownPosition === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'
              }`}
            >
              {options.map((opt) => (
                <button
                  key={opt}
                  onClick={() => {
                    onChange(opt);
                    setIsOpen(false);
                  }}
                  className={`w-full px-4 py-2.5 text-left text-sm transition-colors ${
                    value === opt
                      ? 'bg-amber-500/20 text-amber-500'
                      : 'text-white/70 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  {getDisplayText(opt)}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Flavor Multi-Select Component - Allows selecting up to 3 flavors
const FlavorMultiSelect = ({ label, values, onChange, options, placeholder, maxSelections }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState('bottom');
  const buttonRef = React.useRef(null);

  const handleOpen = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      setDropdownPosition(spaceBelow < 200 && spaceAbove > spaceBelow ? 'top' : 'bottom');
    }
    setIsOpen(!isOpen);
  };

  const handleToggle = (opt) => {
    if (values.includes(opt)) {
      onChange(values.filter(v => v !== opt));
    } else if (values.length < maxSelections) {
      onChange([...values, opt]);
    }
  };

  const displayValue = values.length > 0 
    ? values.join(', ') 
    : placeholder;

  return (
    <div>
      <label className="block text-white/70 text-sm mb-2">
        {label} <span className="text-white/40">(up to {maxSelections})</span>
      </label>
      <div className="relative">
        <button
          ref={buttonRef}
          onClick={handleOpen}
          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-left flex items-center justify-between hover:border-white/20 transition-colors"
          data-testid="filter-select-flavor-profile"
        >
          <span className={values.length > 0 ? 'text-white truncate pr-2' : 'text-white/40'}>
            {displayValue}
          </span>
          <ChevronDown size={18} className={`text-white/40 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
            <div 
              className={`absolute left-0 right-0 bg-[#1a1a1c] border border-white/10 rounded-lg shadow-xl z-20 max-h-52 overflow-y-auto ${
                dropdownPosition === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'
              }`}
            >
              {values.length > 0 && (
                <button
                  onClick={() => onChange([])}
                  className="w-full px-4 py-2.5 text-left text-sm text-amber-500 hover:bg-white/5 border-b border-white/10"
                >
                  Clear selections
                </button>
              )}
              {options.map((opt) => {
                const isSelected = values.includes(opt);
                const isDisabled = !isSelected && values.length >= maxSelections;
                
                return (
                  <button
                    key={opt}
                    onClick={() => !isDisabled && handleToggle(opt)}
                    disabled={isDisabled}
                    className={`w-full px-4 py-2.5 text-left text-sm transition-colors flex items-center justify-between ${
                      isSelected
                        ? 'bg-amber-500/20 text-amber-500'
                        : isDisabled
                        ? 'text-white/30 cursor-not-allowed'
                        : 'text-white/70 hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    <span className="capitalize">{opt}</span>
                    {isSelected && <Check size={16} className="text-amber-500" />}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
      
      {/* Selected flavor chips */}
      {values.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {values.map(flavor => (
            <span 
              key={flavor}
              className="bg-amber-500/20 text-amber-500 text-xs px-2 py-1 rounded-full flex items-center gap-1"
            >
              <span className="capitalize">{flavor}</span>
              <button 
                onClick={() => onChange(values.filter(v => v !== flavor))}
                className="hover:text-amber-300"
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default DiscoverPage;
